/**
 * Google Analytics 4 — storefront ecommerce events.
 * Set measurement ID in Admin → Site → Extra page SEO, or REACT_APP_GA4_MEASUREMENT_ID.
 */

const PURCHASE_DEDUPE_KEY = 'saloni_ga4_purchases_v1';

let activeMeasurementId = '';

/**
 * When true, GA4 is neither loaded nor sent any events. Used to keep signed-in
 * admin/staff browsing out of Google Analytics so customer data stays clean.
 */
let trackingSuppressed = false;

/** Enable/disable all GA4 loading + event sending at runtime. */
export function setAnalyticsSuppressed(suppressed) {
  trackingSuppressed = Boolean(suppressed);
}

/** Whether GA4 tracking is currently suppressed. */
export function isAnalyticsSuppressed() {
  return trackingSuppressed;
}

function readPurchaseDedupeSet() {
  try {
    const raw = localStorage.getItem(PURCHASE_DEDUPE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function markPurchaseTracked(orderNumber) {
  try {
    const id = String(orderNumber || '').trim();
    if (!id) return;
    const set = new Set(readPurchaseDedupeSet());
    set.add(id);
    localStorage.setItem(PURCHASE_DEDUPE_KEY, JSON.stringify([...set].slice(-50)));
  } catch {
    /* ignore */
  }
}

export function resolveGa4MeasurementId(siteSettings) {
  const fromEnv = String(process.env.REACT_APP_GA4_MEASUREMENT_ID || '').trim();
  if (fromEnv && fromEnv.startsWith('G-')) return fromEnv;
  const fromSite = String(siteSettings?.store_copy?.ga4_measurement_id || '').trim();
  if (fromSite && fromSite.startsWith('G-')) return fromSite;
  return '';
}

export function initGA(measurementId) {
  if (trackingSuppressed) return false;
  const id = String(measurementId || '').trim();
  if (!id || !id.startsWith('G-')) return false;
  if (typeof window === 'undefined') return false;
  if (activeMeasurementId === id && typeof window.gtag === 'function') return true;

  if (!document.querySelector(`script[data-ga4="${id}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    script.dataset.ga4 = id;
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  window.gtag('js', new Date());
  window.gtag('config', id, { send_page_view: false });
  activeMeasurementId = id;
  return true;
}

function gtagEvent(name, params) {
  if (trackingSuppressed) return;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function' || !activeMeasurementId) return;
  window.gtag('event', name, params);
}

/** @param {{ categorySlug?: string, productId?: string, name?: string, price?: number, qty?: number }[]} lines */
export function ga4ItemsFromLines(lines) {
  return (lines || []).map((line) => ({
    item_id: String(line.productId || line.id || ''),
    item_name: String(line.name || ''),
    item_category: String(line.categorySlug || line.category || ''),
    price: Number(line.price) || 0,
    quantity: Math.max(1, Number(line.qty) || 1),
  }));
}

export function trackPageView(path) {
  if (trackingSuppressed || !activeMeasurementId) return;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  // GA4 SPA page views must be sent as a `page_view` event; re-calling `config`
  // for the same measurement ID does not reliably record subsequent page views.
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackAddToCart({ product, categorySlug, qty = 1, price }) {
  const unit = Number(price) || 0;
  const amount = Math.max(1, Number(qty) || 1);
  gtagEvent('add_to_cart', {
    currency: 'INR',
    value: unit * amount,
    items: ga4ItemsFromLines([
      {
        productId: product?.id,
        name: product?.name,
        categorySlug,
        price: unit,
        qty: amount,
      },
    ]),
  });
}

export function trackBeginCheckout({ lines, subtotal, coupon }) {
  const items = ga4ItemsFromLines(lines);
  if (!items.length) return;
  const params = {
    currency: 'INR',
    value: Number(subtotal) || 0,
    items,
  };
  if (coupon) params.coupon = String(coupon);
  gtagEvent('begin_checkout', params);
}

/**
 * GA4 purchase — call once on order success. Deduped per order number per browser
 * (localStorage) so refresh or reopening the confirmation in a new tab won't double-count.
 */
export function trackPurchase(payload) {
  const orderNumber = String(payload?.orderNumber || '').trim();
  if (!orderNumber) return;
  if (readPurchaseDedupeSet().includes(orderNumber)) return;

  const items = ga4ItemsFromLines(payload?.lines);
  const total = Number(payload?.total) || 0;
  const shipping = Number(payload?.shipping) || 0;
  const coupon = String(payload?.coupon || '').trim();

  const params = {
    transaction_id: orderNumber,
    currency: 'INR',
    value: total,
    shipping,
    tax: 0,
    items,
  };
  if (coupon) params.coupon = coupon;
  gtagEvent('purchase', params);

  markPurchaseTracked(orderNumber);
}

export function trackViewItem({ product, categorySlug, price }) {
  const unit = Number(price) || 0;
  gtagEvent('view_item', {
    currency: 'INR',
    value: unit,
    items: ga4ItemsFromLines([
      {
        productId: product?.id,
        name: product?.name,
        categorySlug,
        price: unit,
        qty: 1,
      },
    ]),
  });
}

export function trackViewItemList({ products, categorySlug, listName }) {
  if (!products?.length) return;
  const items = products.slice(0, 10).map((p, i) => ({
    index: i,
    item_id: String(p?.id || ''),
    item_name: String(p?.name || ''),
    item_category: String(categorySlug || p?.category || ''),
    price: Number(p?.price) || 0,
    quantity: 1,
  }));
  gtagEvent('view_item_list', {
    item_list_id: String(categorySlug || 'catalog'),
    item_list_name: String(listName || categorySlug || 'Catalog'),
    items,
  });
}

export function trackRemoveFromCart({ product, categorySlug, qty = 1, price }) {
  const unit = Number(price) || 0;
  const amount = Math.max(1, Number(qty) || 1);
  gtagEvent('remove_from_cart', {
    currency: 'INR',
    value: unit * amount,
    items: ga4ItemsFromLines([
      {
        productId: product?.id,
        name: product?.name,
        categorySlug,
        price: unit,
        qty: amount,
      },
    ]),
  });
}

export function trackSelectItem({ product, categorySlug, price, listName }) {
  const unit = Number(price) || 0;
  gtagEvent('select_item', {
    item_list_id: String(categorySlug || 'catalog'),
    item_list_name: String(listName || categorySlug || 'Catalog'),
    items: ga4ItemsFromLines([
      {
        productId: product?.id,
        name: product?.name,
        categorySlug,
        price: unit,
        qty: 1,
      },
    ]),
  });
}

export function trackSearch({ query, resultCount }) {
  if (!String(query || '').trim()) return;
  gtagEvent('search', {
    search_term: String(query).trim(),
    result_count: Number(resultCount) || 0,
  });
}

export function trackViewCart({ lines, subtotal }) {
  const items = ga4ItemsFromLines(lines);
  if (!items.length) return;
  const value =
    subtotal != null
      ? Number(subtotal) || 0
      : items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  gtagEvent('view_cart', {
    currency: 'INR',
    value,
    items,
  });
}

export function trackAddToWishlist({ product, categorySlug, price }) {
  const unit = Number(price) || 0;
  gtagEvent('add_to_wishlist', {
    currency: 'INR',
    value: unit,
    items: ga4ItemsFromLines([
      {
        productId: product?.id,
        name: product?.name,
        categorySlug,
        price: unit,
        qty: 1,
      },
    ]),
  });
}

export function trackAddShippingInfo({ lines, subtotal, shippingTier, coupon }) {
  const items = ga4ItemsFromLines(lines);
  if (!items.length) return;
  const params = {
    currency: 'INR',
    value: Number(subtotal) || 0,
    items,
  };
  if (shippingTier) params.shipping_tier = String(shippingTier);
  if (coupon) params.coupon = String(coupon);
  gtagEvent('add_shipping_info', params);
}

export function trackAddPaymentInfo({ lines, subtotal, paymentType, coupon }) {
  const items = ga4ItemsFromLines(lines);
  if (!items.length) return;
  const params = {
    currency: 'INR',
    value: Number(subtotal) || 0,
    items,
  };
  if (paymentType) params.payment_type = String(paymentType);
  if (coupon) params.coupon = String(coupon);
  gtagEvent('add_payment_info', params);
}

export function trackLogin(method = 'password') {
  gtagEvent('login', { method: String(method || 'password') });
}

export function trackSignUp(method = 'password') {
  gtagEvent('sign_up', { method: String(method || 'password') });
}
